<template>
  <v-card class="pa-4" outlined>
    <v-card-title class="headline mb-2"> Editor de Expresiones </v-card-title>

    <v-card-subtitle class="mb-4">
      Construye expresiones usando variables seleccionadas y la expresiones de la biblioteca.
      <v-chip
        class="ma-1"
        outlined
        small
        color="deep-purple"
        text-color="deep-purple"
        @click="mostrarAyuda()">
        <span class="font-weight-bold mr-1"> Ver Instrucciones </span>
      </v-chip>
    </v-card-subtitle>

    <v-row>
      <!-- COLUMNA IZQUIERDA: VARIABLES SELECCIONADAS -->
      <v-col cols="12" md="4">
        <h3 class="subtitle-1 mb-2">Variables seleccionadas</h3>

        <!-- BUSCADOR VARIABLES -->
        <v-text-field
          v-model="busquedaVariables"
          dense
          outlined
          hide-details
          label="Buscar variable"
          prepend-inner-icon="mdi-magnify"
          class="mb-3" />

        <div
          class="pa-2"
          style="height: 700px; overflow-y: auto; border: 1px solid #eee; border-radius: 8px">
          <v-alert
            v-if="!variables || variables.length === 0"
            type="info"
            border="left"
            colored-border
            elevation="1">
            {{ $t('bannerMessages.noVariables') }}
          </v-alert>

          <div v-else class="d-flex flex-wrap">
            <v-chip
              v-for="variable in variablesFiltradas"
              :key="variable.fullPath"
              class="ma-1"
              outlined
              small
              color="deep-purple"
              text-color="deep-purple"
              @click="insertarVariableEnExpresion(variable)">
              <span class="font-weight-bold mr-1">
                {{ variable.alias || variable.fullPath }}
              </span>
              <span class="grey--text text--darken-1">
                ({{ formatearValorVariable(variable.value) }})
              </span>
            </v-chip>
          </div>
        </div>
      </v-col>

      <!-- COLUMNA CENTRAL: EDITOR Y LISTADO DE EXPRESIONES -->
      <v-col cols="12" md="4">
        <!-- PARTE SUPERIOR -->
        <div class="mb-6">
          <h3 class="subtitle-1 mb-2">Construcción de expresión</h3>

          <div class="mb-2">
            <v-chip small :color="modoEdicion.color" :text-color="modoEdicion.textColor">
              {{ modoEdicion.texto }}
            </v-chip>
          </div>

          <!-- LABEL VALOR REAL / VISTA PREVIA -->
          <div class="mb-2">
            <v-chip
              small
              :color="vistaPreviaError ? 'red lighten-5' : 'deep-purple lighten-5'"
              :text-color="vistaPreviaError ? 'red darken-2' : 'deep-purple darken-2'">
              Valor real / vista previa:
              <strong class="ml-1">
                {{ vistaPreviaError ? vistaPreviaError : vistaPreviaValor }}
              </strong>
            </v-chip>
          </div>

          <!-- NOMBRE DE LA FUNCIÓN -->
          <v-text-field
            v-model="nombreExpresionActual"
            dense
            outlined
            hide-details="auto"
            label="Nombre de la Expresión"
            placeholder="Ej: codigoIdentificacion"
            :error-messages="errorNombreFuncion"
            class="mb-3" />

          <!-- TEXTAREA EXPRESIÓN -->
          <v-textarea
            v-model="expresionActual"
            ref="campoExpresion"
            dense
            outlined
            hide-details
            class="editor-expresion"
            label="Expresión de la función"
            placeholder="Escribe o construye tu expresión aquí..."
            auto-grow
            rows="10"
            rows-max="20"
            @input="actualizarVistaPrevia" />

          <!-- BOTONES GUARDAR / ELIMINAR / LIMPIAR / VALIDAR -->
          <div class="mt-4 d-flex align-center flex-wrap">
            <v-btn
              small
              color="deep-purple"
              class="white--text mr-2 mb-2"
              :disabled="!expresionActual"
              @click="guardarExpresion">
              Guardar expresión
            </v-btn>

            <v-btn
              small
              color="blue darken-1"
              class="white--text mr-2 mb-2"
              :disabled="!expresionActual"
              @click="clonarExpresion">
              Clonar
            </v-btn>

            <v-btn
              small
              color="red lighten-1"
              class="white--text mr-2 mb-2"
              :disabled="!expresionEnEdicion"
              @click="eliminarExpresion">
              Eliminar expresión
            </v-btn>

            <v-btn small outlined color="grey" class="mr-2 mb-2" @click="limpiarEditor">
              Limpiar
            </v-btn>

            <v-btn
              small
              outlined
              color="orange darken-2"
              class="mr-2 mb-2"
              @click="validarExpresiones">
              Validar
            </v-btn>

            <v-btn small outlined color="green darken-2" class="mb-2" @click="descargarPlantilla">
              Descargar
            </v-btn>
          </div>
        </div>

        <!-- PARTE INFERIOR: LISTADO EXPRESIONES REGISTRADAS -->
        <div>
          <h3 class="subtitle-1 mb-2">Expresiones registradas ({{ expresionesLocales.length }})</h3>

          <v-alert
            v-if="expresionesLocales.length === 0"
            type="info"
            border="left"
            colored-border
            elevation="1">
            {{ $t('bannerMessages.noExpressions') }}
          </v-alert>

          <div v-else class="d-flex flex-wrap">
            <v-tooltip
              v-for="expresion in expresionesLocales"
              :key="expresion.id"
              bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="ma-1"
                  small
                  :color="'deep-purple lighten-5'"
                  :text-color="'deep-purple darken-2'"
                  v-bind="attrs"
                  v-on="on"
                  @click="seleccionarFuncion(expresion)">
                  {{ expresion.id }}
                </v-chip>
              </template>
              <span>{{ expresion.expresion }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-col>

      <!-- COLUMNA DERECHA: BIBLIOTECA DE FUNCIONES -->
      <v-col cols="12" md="4">
        <h3 class="subtitle-1 mb-2">Biblioteca de funciones</h3>

        <!-- BUSCADOR BIBLIOTECA -->
        <v-text-field
          v-model="busquedaFuncionesBiblioteca"
          dense
          outlined
          hide-details
          label="Buscar en biblioteca"
          prepend-inner-icon="mdi-magnify"
          class="mb-3" />

        <div
          class="pa-2"
          style="height: 700px; overflow-y: auto; border: 1px solid #eee; border-radius: 8px">
          <v-list dense>
            <v-list-item
              v-for="funcion in bibliotecaFuncionesFiltradas"
              :key="funcion.nombre"
              class="mb-2">
              <v-list-item-content>
                <div class="d-flex align-center mb-1">
                  <v-chip
                    small
                    outlined
                    color="deep-purple"
                    text-color="deep-purple"
                    @click="seleccionarFuncionBiblioteca(funcion)">
                    {{ funcion.nombre }}
                  </v-chip>
                  <v-tooltip
                    v-if="
                      (funcion.infoTecnica && funcion.infoTecnica.parametros) ||
                      (funcion.infoExtendida && funcion.infoExtendida.length)
                    "
                    bottom
                    max-width="360">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        class="ml-2"
                        small
                        outlined
                        color="blue darken-1"
                        text-color="blue darken-1"
                        v-bind="attrs"
                        v-on="on">
                        i
                      </v-chip>
                    </template>
                    <div class="info-tooltip">
                      <template v-if="funcion.infoTecnica">
                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Fórmula</div>
                          <code>{{ funcion.infoTecnica.formula }}</code>
                        </div>

                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Parámetros</div>
                          <div
                            v-for="(param, idx) in funcion.infoTecnica.parametros"
                            :key="`param-${idx}`"
                            class="mb-1">
                            {{ idx + 1 }}. {{ param.nombre }} (req:
                            {{ param.requerido ? 'sí' : 'no' }}, exacto:
                            {{ param.exacto ? 'sí' : 'no' }}): {{ param.descripcion }}
                          </div>
                        </div>

                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Procesamiento</div>
                          <div
                            v-for="(linea, idx) in funcion.infoTecnica.procesamiento"
                            :key="`proc-${idx}`"
                            class="mb-1">
                            {{ idx + 1 }}. {{ linea }}
                          </div>
                        </div>

                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Retorno</div>
                          <div class="mb-1">{{ funcion.infoTecnica.retorno }}</div>
                        </div>

                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Ejemplo</div>
                          <code>{{ funcion.infoTecnica.ejemplo }}</code>
                        </div>

                        <div class="info-section">
                          <div class="font-weight-bold mb-1">Uso con otras funciones</div>
                          <code>{{ funcion.infoTecnica.usoEnOtraFuncion }}</code>
                          <div class="caption grey--text text--darken-1">
                            Las funciones anidadas deben ir entre paréntesis.
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="font-weight-bold mb-1">
                          {{ funcion.infoTitulo || 'Info técnica' }}
                        </div>
                        <div
                          v-for="(linea, idx) in funcion.infoExtendida"
                          :key="idx"
                          class="mb-1">
                          {{ linea }}
                        </div>
                      </template>
                    </div>
                  </v-tooltip>
                </div>

                <div class="caption grey--text text--darken-2 mb-1">
                  {{ funcion.descripcion }}
                </div>

                <div class="caption">
                  <span class="grey--text text--darken-1">Ejemplo: </span>
                  <code>{{ funcion.ejemplo }}</code>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="mostrarDialogoAyuda" max-width="800px">
      <v-card>
        <v-card-title class="headline">
          {{ ayuda.titulo }}
        </v-card-title>

        <v-card-text>
          <p class="mb-4">
            {{ ayuda.descripcion }}
          </p>

          <div v-for="(seccion, index) in ayuda.secciones" :key="index" class="mb-4">
            <h3 class="subtitle-1 font-weight-bold mb-2">
              {{ seccion.titulo }}
            </h3>

            <ul class="mb-2" v-if="seccion.contenido && seccion.contenido.length">
              <li v-for="(linea, idx) in seccion.contenido" :key="idx">
                {{ linea }}
              </li>
            </ul>

            <div v-if="seccion.ejemplos && seccion.ejemplos.length">
              <div class="caption grey--text text--darken-1 mb-1">Ejemplos:</div>
              <div v-for="(ejemplo, j) in seccion.ejemplos" :key="j" class="mb-1">
                <code>{{ ejemplo }}</code>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="deep-purple" @click="cerrarAyuda"> Cerrar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { esBase64Imagen } from '@/util/libreriaFormatos/imagenes/base64.js'
import BibliotecaDeFunciones from '~/util/bibliotecaDeFunciones'

function serializarInfoTecnica(info) {
  if (!info) return ''
  const partes = []
  if (info.formula) partes.push(info.formula)
  if (Array.isArray(info.parametros)) {
    info.parametros.forEach((p) => {
      partes.push(
        `${p.nombre} requerido:${p.requerido ? 'si' : 'no'} exacto:${
          p.exacto ? 'si' : 'no'
        } ${p.descripcion || ''}`
      )
    })
  }
  if (Array.isArray(info.procesamiento)) partes.push(info.procesamiento.join(' '))
  if (info.retorno) partes.push(info.retorno)
  if (info.ejemplo) partes.push(info.ejemplo)
  if (info.usoEnOtraFuncion) partes.push(info.usoEnOtraFuncion)
  return partes.join(' ')
}

export default {
  name: 'EditorExpresiones',

  props: {
    // keynames.variables
    variables: {
      type: Array,
      required: true,
    },
    // keynames.expresiones
    expresiones: {
      type: Array,
      default: () => [],
    },
    // función externa para mostrar el "valor real"
    evaluadorExpresion: {
      type: Function,
      default: null,
    },
  },

  data() {
    const funciones = BibliotecaDeFunciones.obtenerFunciones()
    return {
      ayuda: BibliotecaDeFunciones.obtenerAyudaSintaxisGeneral(),
      mostrarDialogoAyuda: false,
      bibliotecaFunciones: funciones.map((funcion) => ({
        nombre: funcion.nombreFuncion,
        descripcion: funcion.descripcion,
        ejemplo: funcion.ejemplo,
        sintaxisBase: funcion.plantillaExpresion,
        categoria: funcion.categoria || 'General',
        infoExtendida: funcion.infoExtendida || null,
        infoTitulo: funcion.infoTitulo || 'Info técnica',
        infoTecnica: funcion.infoTecnica || null,
        infoTecnicaTexto: serializarInfoTecnica(funcion.infoTecnica),
      })),
      // editor
      nombreExpresionActual: '',
      expresionActual: '',
      vistaPreviaValor: 'Sin vista previa',
      vistaPreviaError: '',
      expresionEnEdicion: null,
      variableEnEdicion: null,
      errorNombreFuncion: [],

      // listas
      expresionesLocales: [],

      // búsquedas
      busquedaVariables: '',
      busquedaFuncionesBiblioteca: '',
    }
  },

  computed: {
    modoEdicion() {
      if (this.expresionEnEdicion) {
        const nombre = (this.expresionEnEdicion.id || this.nombreExpresionActual || '').trim()
        return {
          texto: `Editando: ${nombre || 'Sin nombre'}`,
          color: 'orange lighten-4',
          textColor: 'orange darken-3',
        }
      }
      return {
        texto: 'Nueva expresión',
        color: 'green lighten-4',
        textColor: 'green darken-3',
      }
    },

    variablesFiltradas() {
      return this.filtrarPorTexto(
        this.variables || [],
        (v) =>
          `${v.alias || ''} ${v.fullPath || ''} ${
            v.value !== null && v.value !== undefined ? v.value : ''
          }`,
        this.busquedaVariables
      )
    },

    bibliotecaFuncionesFiltradas() {
      return this.filtrarPorTexto(
        this.bibliotecaFunciones || [],
        (f) =>
          `${f.nombre} ${f.descripcion} ${f.ejemplo} ${
            f.infoTecnicaTexto || ''
          } ${(f.infoExtendida || []).join(' ')}`,
        this.busquedaFuncionesBiblioteca
      )
    },
  },

  watch: {
    expresiones: {
      immediate: true,
      deep: true,
      handler(nuevas) {
        this.expresionesLocales = (nuevas || []).map((e) => ({
          id: e.id,
          expresion: e.expresion,
        }))
      },
    },
  },

  methods: {
    // -----------------------
    // BÚSQUEDA / FILTRADO
    // -----------------------
    filtrarPorTexto(lista, obtenerTexto, termino) {
      if (!termino) return lista
      const buscado = termino.toString().toLowerCase()

      return (lista || []).filter((item) => {
        const texto = (obtenerTexto(item) || '').toString().toLowerCase()
        return texto.includes(buscado)
      })
    },

    // -----------------------
    // EDITOR
    // -----------------------
    insertarVariableEnExpresion(variable) {
      this.variableEnEdicion = variable

      const alias = (variable.alias || '').trim()

      // 1️⃣ Si NO tiene alias → NO permitir insertar
      if (!alias) {
        this.$store.commit('notification/show', {
          text: this.$t('bannerMessages.variableNeedsAlias', { variable: variable.fullPath }),
          color: 'warning',
          timeout: 3000,
        })
        return
      }

      const fullPath = variable.fullPath
      const expresion = variable.expresion // ya contiene ${v:fullPath} o ${i:fullPath}

      // 2️⃣ Texto que va SI HAY selección → alias obligatorio
      const tokenSeleccion = alias

      // 3️⃣ Texto que va SIN selección → ${alias}
      const tokenSinSeleccion = `\${${alias}}`

      // 4️⃣ Obtener textarea real
      const textarea =
        this.$refs.campoExpresion &&
        this.$refs.campoExpresion.$el &&
        this.$refs.campoExpresion.$el.querySelector('textarea')

      const original = this.expresionActual || ''

      if (textarea && typeof textarea.selectionStart === 'number') {
        const start = textarea.selectionStart
        const end = textarea.selectionEnd

        if (start !== end) {
          // 📝 HAY selección → reemplazar por alias
          this.expresionActual = original.slice(0, start) + tokenSeleccion + original.slice(end)

          this.$nextTick(() => {
            const pos = start + tokenSeleccion.length
            textarea.selectionStart = textarea.selectionEnd = pos
          })
        } else {
          // ➕ SIN selección → insertar ${alias}
          this.expresionActual =
            original.slice(0, start) + tokenSinSeleccion + original.slice(start)

          this.$nextTick(() => {
            const pos = start + tokenSinSeleccion.length
            textarea.selectionStart = textarea.selectionEnd = pos
          })
        }
      } else {
        // Fallback sin control de cursor
        this.expresionActual = original ? `${original} ${tokenSinSeleccion}` : tokenSinSeleccion
      }

      this.actualizarVistaPrevia()
    },
    esValorBase64(valor) {
      return esBase64Imagen(valor)
    },
    formatearValorVariable(valor) {
      if (this.esValorBase64(valor)) return 'Imagen detectada (base64)'
      return valor
    },

    validarNombreFuncion() {
      const errores = []
      const nombre = (this.nombreExpresionActual || '').trim()

      if (!nombre) {
        errores.push('Debes indicar un nombre para la función.')
      }

      // Comprobamos duplicados dentro del listado local
      const yaExiste = (this.expresionesLocales || []).some((f) => {
        const nombreFuncion = (f.nombre || f.id || '').trim()
        if (!nombreFuncion) return false

        // Si estamos editando, permitimos que coincida con la misma función
        if (this.expresionEnEdicion && f === this.expresionEnEdicion) return false

        return nombreFuncion.toLowerCase() === nombre.toLowerCase()
      })

      if (yaExiste) {
        errores.push('Ya existe una función con ese nombre en este formato.')
      }

      this.errorNombreFuncion = errores
      return errores.length === 0
    },

    guardarExpresion() {
      if (!this.expresionActual) return
      if (!this.validarNombreFuncion()) return

      const nombreNormalizado = this.nombreExpresionActual.trim()

      if (this.expresionEnEdicion) {
        // Editar
        this.expresionEnEdicion.id = nombreNormalizado
        this.expresionEnEdicion.expresion = this.expresionActual
      } else {
        // Nueva
        const nueva = {
          id: nombreNormalizado,
          expresion: this.expresionActual,
        }
        this.expresionesLocales.push(nueva)
        this.expresionEnEdicion = nueva
      }
      // Notificar al padre: actualiza keynames.funciones
      this.$emit(
        'update:expresiones',
        this.expresionesLocales.map((f) => ({
          id: f.id,
          expresion: f.expresion,
        }))
      )

      // Además, registrar la expresión dentro de la variable asociada
      if (this.variableEnEdicion) {
        this.$emit('actualizar-expresion-variable', {
          expresion: this.expresionActual,
        })
      }

      // Limpiar editor después de guardar
      this.limpiarEditor()
    },

    clonarExpresion() {
      if (!this.expresionActual) return

      const base = (this.nombreExpresionActual || this.expresionEnEdicion?.id || 'expresion').trim()
      const nombreClonado = this.generarNombreClonado(base)

      const nueva = {
        id: nombreClonado,
        expresion: this.expresionActual,
      }

      this.expresionesLocales.push(nueva)
      this.expresionEnEdicion = nueva
      this.variableEnEdicion = null
      this.nombreExpresionActual = nombreClonado
      this.errorNombreFuncion = []

      this.$emit(
        'update:expresiones',
        this.expresionesLocales.map((f) => ({
          id: f.id,
          expresion: f.expresion,
        }))
      )

      this.actualizarVistaPrevia()
    },

    eliminarExpresion() {
      if (!this.expresionEnEdicion) return

      const id = this.expresionEnEdicion.id
      this.expresionesLocales = this.expresionesLocales.filter((f) => f.id !== id)

      this.expresionEnEdicion = null
      this.expresionActual = ''
      this.nombreExpresionActual = ''
      this.vistaPreviaValor = 'Sin vista previa'
      this.errorNombreFuncion = []

      this.$emit('update:expresiones', this.expresionesLocales)
    },

    seleccionarFuncion(funcion) {
      this.expresionEnEdicion = funcion
      this.expresionActual = funcion.expresion || ''
      this.nombreExpresionActual = (funcion.id || '').trim()
      this.variableEnEdicion = null
      this.errorNombreFuncion = []
      this.actualizarVistaPrevia()
    },

    seleccionarFuncionBiblioteca(funcionBiblioteca) {
      this.expresionEnEdicion = null
      this.expresionActual = funcionBiblioteca.sintaxisBase || funcionBiblioteca.ejemplo

      if (!this.nombreExpresionActual) {
        this.nombreExpresionActual = funcionBiblioteca.nombre
      }
      this.errorNombreFuncion = []
      this.actualizarVistaPrevia()
    },

    limpiarEditor() {
      this.expresionEnEdicion = null
      this.variableEnEdicion = null
      this.expresionActual = ''
      this.nombreExpresionActual = ''
      this.vistaPreviaValor = 'Sin vista previa'
      this.errorNombreFuncion = []
    },

    async actualizarVistaPrevia() {
      if (this.evaluadorExpresion && typeof this.evaluadorExpresion === 'function') {
        try {
          const resultado = this.evaluadorExpresion(this.expresionActual)
          const resFinal =
            resultado && typeof resultado.then === 'function' ? await resultado : resultado

          if (resFinal && typeof resFinal === 'object' && 'error' in resFinal) {
            this.vistaPreviaError = resFinal.error || ''
            this.vistaPreviaValor = resFinal.value || 'Sin resultado'
          } else {
            this.vistaPreviaError = ''
            this.vistaPreviaValor = resFinal != null ? resFinal : 'Sin resultado'
          }
        } catch (e) {
          this.vistaPreviaError = 'Error al evaluar expresión'
          this.vistaPreviaValor = 'Sin resultado'
        }
      } else {
        this.vistaPreviaError = 'Vista previa no disponible'
        this.vistaPreviaValor = 'Sin resultado'
      }
    },

    // -----------------------
    // VALIDACIÓN DE CONSISTENCIA
    // -----------------------
    validarExpresiones() {
      alert(
        this.$t('bannerMessages.expressionValidationInfo')
      )
    },

    descargarPlantilla() {
      alert(
        this.$t('bannerMessages.expressionDownloadInfo')
      )
    },

    generarIdFuncion() {
      return 'func_' + Math.random().toString(36).substr(2, 9)
    },

    generarNombreClonado(base) {
      const nombreBase = base || 'expresion'
      const existe = (nombre) =>
        (this.expresionesLocales || []).some(
          (f) => (f.id || '').trim().toLowerCase() === nombre.toLowerCase()
        )

      let candidato = `${nombreBase}_nuevo`
      if (!existe(candidato)) return candidato

      let i = 2
      while (existe(`${nombreBase}_nuevo${i}`)) i++
      return `${nombreBase}_nuevo${i}`
    },

    obtenerVariablePorFullPath(fullPath) {
      if (!fullPath || !this.variables) return null
      return this.variables.find((v) => v.fullPath === fullPath) || null
    },
    mostrarAyuda() {
      this.mostrarDialogoAyuda = true
    },

    cerrarAyuda() {
      this.mostrarDialogoAyuda = false
    },
  },
}
</script>

<style scoped>
code {
  font-size: 0.75rem;
}
.editor-expresion textarea {
  font-family: monospace;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre;
}
.info-tooltip {
  max-width: 360px;
  white-space: normal;
}
.info-section {
  margin-bottom: 8px;
}
</style>
